import type { Link } from '@admin/libs/types'
import type { PopupEvent } from '@scripts/frontend/libs/types/site-configuration'

import {
  Image,
  Modal,
  ModalBody,
  ModalContent,
  useDisclosure,
} from '@nextui-org/react'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { useEffect } from 'react'
import { useCookies } from 'react-cookie'

dayjs.extend(isBetween)

export type PopupEventItemProps = PopupEvent

export function PopupEventItem({
  endDate,
  id,
  images,
  link,
  name,
  startDate,
}: PopupEventItemProps) {
  const { isOpen, onClose, onOpen, onOpenChange } = useDisclosure()
  const cookieName = `popup-event-${id}-closed`
  const [cookies, setCookie] = useCookies([cookieName])

  // Check if the current date is between the start and end dates
  const isBetweenDates = dayjs().isBetween(dayjs(startDate), dayjs(endDate))
  // Check if the popup has been closed before
  const isClosed = cookies[cookieName] ?? false

  useEffect(() => {
    // Open the modal if the popup is not closed and the current date is within the event period
    if (!isClosed && isBetweenDates) {
      onOpen()
    }
  }, [isBetweenDates, isClosed, onOpen])

  // Return null if the popup is closed or the current date is outside the event period
  if (isClosed || !isBetweenDates) {
    return null
  }

  const handleOnClose = () => {
    // Set a cookie to mark the popup as closed, expiring in 1 day
    const expireDate = dayjs().add(1, 'day').toDate()
    setCookie(cookieName, true, { expires: expireDate, path: '/' })
    onClose()
  }

  const handleOnOpen = ({ isNewTab, url }: Link) => {
    const expireDate = dayjs().add(1, 'day').toDate()
    setCookie(cookieName, true, { expires: expireDate, path: '/' })
    window.open(url, isNewTab ? '_blank' : '_self')
  }

  const renderImages = () => (
    <>
      <Image
        alt={images.desktop?.alt}
        className="hidden md:block"
        src={images.desktop?.sizes['full']}
      />
      <Image
        alt={images.desktop?.alt}
        className="block md:hidden"
        src={images.mobile?.sizes['full']}
      />
    </>
  )

  return (
    <Modal
      classNames={{
        base: 'p-0',
        body: 'p-0',
        closeButton: 'rounded-none top-4 right-4 text-2xl z-20',
      }}
      isOpen={isOpen}
      onClose={handleOnClose}
      onOpenChange={onOpenChange}
      placement="center"
      size="5xl"
    >
      <ModalContent>
        <ModalBody>
          {link ? (
            <a
              href="#"
              onClick={() => handleOnOpen(link)}
              rel="noreferrer"
              title={link?.linkText}
            >
              {renderImages()}
            </a>
          ) : (
            renderImages()
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default PopupEventItem
